import { motion } from 'framer-motion';
import { TrophyIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CARD_STYLES } from '../constants';

import { Button } from '@/components';
import { Paginator } from '@/components/Paginator';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTapTourney from '@/hooks/useTapTourney';
import ROUTES from '@/router/routes';
import { ParticipacionTapTo } from '@/types/tda';
import { FADE_IN_OUT, formatNumberToLocale } from '@/utils';

interface TapTourneyListProps {
  tourneyList: ParticipacionTapTo[] | undefined;
}

const cardStyle = (item: ParticipacionTapTo | number) => {
  if (typeof item === 'number') {
    return CARD_STYLES.tap1;
  }
  switch (item.tipoPinTA) {
    case 21:
      return CARD_STYLES.tap1;
    case 20:
      return CARD_STYLES.tap2;
    case 19:
      return CARD_STYLES.tap3;
    default:
      return CARD_STYLES.tap1;
  }
};

const TourneyItem = ({ item }: { item: ParticipacionTapTo }) => {
  const navigate = useNavigate();
  const { tapTourneyData, handleSetTapTourney } = useTapTourney();
  return (
    <div
      className={`flex items-center content-between justify-between px-7 py-3`}
    >
      <div className="flex items-center">
        <div
          className={`w-8 h-8 ${
            cardStyle(item).cupBg
          } flex justify-center items-center rounded-full mr-4`}
        >
          <TrophyIcon
            className={`${cardStyle(item).textAndCupColor}`}
            size={20}
          />
        </div>
        <p className="font-bold">{item.nombreTA}</p>
      </div>
      <p className="font-semibold text-[#717077]">{`Participás por $${formatNumberToLocale(
        item.premio ?? 0
      )}`}</p>
      <Button
        className={`w-[115px] h-[36px] px-0 text-xs border-none ${
          cardStyle(item).buttonColor
        }`}
        type="button"
        variant="primary"
        onClick={() => {
          handleSetTapTourney({
            ...tapTourneyData,
            tourneyDate: item.fechaInicio,
            tourneyId: item.idTA,
            tourneyPrize: item.premio,
          });
          navigate(ROUTES.viewTapTourney);
        }}
      >
        Ver Torneo
      </Button>
    </div>
  );
};

const TourneyItemMobile = ({ item }: { item: ParticipacionTapTo }) => {
  const navigate = useNavigate();
  const { tapTourneyData, handleSetTapTourney } = useTapTourney();
  return (
    <div className="bg-white rounded-lg mb-6 p-4">
      <div className="flex ">
        <div
          className={`w-8 h-8 mr-4 ${
            cardStyle(item).cupBg
          } flex justify-center items-center rounded-full`}
        >
          <TrophyIcon
            className={`${cardStyle(item).textAndCupColor}`}
            size={20}
          />
        </div>
        <div>
          <p className="font-bold text-black pb-1 text-[14px]">
            {item.nombreTA}
          </p>
          <p className="font-medium text-[14px] text-[#4F4F4F]">{`Premio: $${formatNumberToLocale(
            item.premio ?? 0
          )}`}</p>
          <p className="font-semibold text-[12px] text-[#A09EB3] uppercase">{`DESDE ${item.fechaInicio.nombre}`}</p>
        </div>
      </div>
      <div className="flex justify-center mt-4">
        <Button
          className={`w-[115px] h-[36px] px-0 text-xs border-none ${
            cardStyle(item).buttonColor
          }`}
          type="button"
          variant="primary"
          onClick={() => {
            handleSetTapTourney({
              ...tapTourneyData,
              tourneyDate: item.fechaInicio,
              tourneyId: item.idTA,
              tourneyPrize: item.premio,
            });
            navigate(ROUTES.viewTapTourney);
          }}
        >
          Ver Torneo
        </Button>
      </div>
    </div>
  );
};

const TapTourneyList = ({ tourneyList }: TapTourneyListProps) => {
  const { isDesktop, isMobile } = useIsDesktop();
  const [activeData, setActiveData] = useState<ParticipacionTapTo[]>([]);
  const [contentCurrentPage, setContentCurrentPage] = useState<
    ParticipacionTapTo[]
  >([]);
  const [dataVersion, setDataVersion] = useState(0);

  const initState = useCallback(() => {
    const data = tourneyList || [];
    setActiveData(data);
    if (data.length > 0 && isDesktop) {
      setContentCurrentPage(data.slice(0, 10));
    } else {
      setContentCurrentPage(data);
    }
  }, [tourneyList, isDesktop]);

  // Inicializar estado cuando cambian los datos
  useEffect(() => {
    initState();

    return () => {
      setContentCurrentPage([]);
      setActiveData([]);
    };
  }, [initState]);

  // Actualizar cuando cambia entre móvil y escritorio
  useEffect(() => {
    if (activeData.length > 0) {
      setContentCurrentPage(
        activeData.slice(0, isDesktop ? 10 : activeData.length)
      );
      setDataVersion((prev) => prev + 1);
    }
  }, [isDesktop, activeData]);

  const handleContentCurrentPage = (dts: ParticipacionTapTo[]) => {
    dts && setContentCurrentPage(dts);
  };

  return (
    <div className="w-[calc(100%+2rem)] -left-4 relative lg:left-auto lg:w-auto">
      <h1 className="mt-8 font-bold">Estás participando</h1>

      {isDesktop && activeData && activeData.length > 0 && (
        <motion.div animate={'show'} initial={'hide'} variants={FADE_IN_OUT}>
          <div className="lg:h-auto mt-6 rounded-lg bg-white">
            {isDesktop &&
              contentCurrentPage.map(
                (item: ParticipacionTapTo, index: number) => (
                  <TourneyItem
                    key={`${index}-${Math.random()
                      .toString(36)
                      .substring(2, 15)}`}
                    item={item}
                  />
                )
              )}

            {isDesktop && activeData.length > 9 && (
              <Paginator
                key={`tourneys-${dataVersion}`}
                currentContentPage={(items) => handleContentCurrentPage(items)}
                items={activeData}
              />
            )}
          </div>
        </motion.div>
      )}
      <div className="mt-6">
        {isMobile &&
          (tourneyList ?? []).map((item: ParticipacionTapTo, index: number) => (
            <TourneyItemMobile
              key={`${index}-${Math.random().toString(36).substring(2, 15)}`}
              item={item}
            />
          ))}
      </div>
    </div>
  );
};

export default TapTourneyList;
